<template>
  <div>

    <!-- Tag Library Slideover -->
    <slide-over-panel v-model:show="showTagLibrary" width="max-w-2xl" z-index="z-[1000]">
      <template #title>Tag Library</template>
      <template #title-content>Manage your custom tags. System tags are read-only.</template>
      <template #content>
        <div>
          <div class="flex justify-end mb-4">
            <button
                @click="openCreateModal()"
                class="inline-flex items-center gap-2 justify-center px-8 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
            >
                <svg xmlns="http://www.w3.org/2000/svg" style="width:12px;height:12px" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              New Tag
            </button>
          </div>

          <!-- Suggestions -->
          <div v-if="tagSuggestions.length > 0" class="mb-4">
            <p class="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2">Suggested tags</p>
            <div class="flex flex-wrap gap-2">
              <button
                  v-for="s in tagSuggestions"
                  :key="s.label"
                  type="button"
                  class="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full border border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                  @click="openCreateModal(s)"
              >+ {{ s.label }}</button>
            </div>
          </div>
          <div class="bg-white rounded-md border border-gray-200 overflow-hidden">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="m-0 p-0 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th class="m-0 p-0 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                  <th class="m-0 p-0 px-6 py-3"></th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="tagsLoading">
                  <td colspan="3" class="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
                <tr v-else-if="allTags.length === 0">
                  <td colspan="3" class="px-6 py-8 text-center text-gray-500">No custom tags yet.</td>
                </tr>
                <tr v-for="tag in allTags" :key="tag.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <span
                        class="inline-flex items-center px-3 py-0.5 rounded-full text-sm"
                        :style="tagStyle(tag.color)"
                    >{{ tag.name }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ tag.count }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button @click="openEditModal(tag)" class="text-gray-400 hover:text-blue-600 mr-3 transition-colors" title="Edit">
                      <span class="lnr icon-pencil" style="font-size: 0.85rem !important;"></span>
                    </button>
                    <button @click="confirmDelete(tag)" class="text-gray-400 hover:text-red-600 transition-colors" title="Delete">
                      <span class="lnr icon-trash2" style="font-size: 0.85rem !important;"></span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </slide-over-panel>

    <!-- Bulk Assign Section -->
    <div>

      <div v-if="treeLoadError" class="rounded-md border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700">
        {{ treeLoadError }}
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <!-- Tree panel -->
        <div class="lg:col-span-2 bg-white rounded-md border border-gray-200">

        <!-- Search + controls bar -->
        <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
          <input
              v-model="treeSearch"
              type="text"
              placeholder="Search areas, zones, or points"
              class="flex-1 rounded-md border-gray-300 bg-white px-3 py-2 text-sm transition-colors focus:border-blue-500 focus:ring-blue-500"
          />
          <button
              type="button"
              class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              @click="clearSelection"
          >Clear</button>
          <button
              type="button"
              class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              @click="toggleAllExpanded"
          >{{ allExpanded ? 'Collapse all' : 'Expand all' }}</button>
        </div>

        <!-- Selection count bar -->
        <div
            v-if="selectedMeterIds.size > 0"
            class="flex items-center gap-3 px-4 h-10 border-b border-gray-100 bg-blue-50"
        >
          <span class="text-sm font-medium text-blue-900">
            {{ selectedMeterIds.size }} {{ selectedMeterIds.size === 1 ? 'point' : 'points' }} selected
          </span>
        </div>

        <!-- Loading -->
        <div v-if="treeLoading" class="flex min-h-64 items-center justify-center p-8">
          <div class="flex flex-col items-center space-y-1">
            <span class="lnr icon-hourglass text-4xl text-blue-500 mb-2"></span>
            <span class="text-base font-medium">Loading points</span>
            <span class="text-sm text-gray-500">Just a moment...</span>
          </div>
        </div>

        <!-- Empty -->
        <div v-else-if="filteredTree.length === 0" class="flex min-h-64 items-center justify-center p-8">
          <div class="flex flex-col items-center space-y-1">
            <span class="lnr icon-inbox text-4xl text-gray-400 mb-2"></span>
            <span class="text-base font-medium">{{ treeSearch.trim() ? 'No matching points' : 'No points available' }}</span>
            <span class="text-sm text-gray-500">{{ treeSearch.trim() ? 'Try a different search term.' : 'No accessible points found.' }}</span>
          </div>
        </div>

        <!-- Tree -->
        <ul v-else class="divide-y divide-gray-100">
          <template v-for="(area, areaIdx) in filteredTree" :key="'a-' + area.id">
            <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
              <div
                  class="flex items-center gap-2 pr-4 py-3 pl-2 sm:px-4 font-medium text-gray-900 hover:bg-gray-100 select-none"
              >
                <span class="text-gray-400 cursor-pointer p-1 -m-1" @click="toggleAreaExpanded(area)">
                  <svg v-if="expandedAreas.has(area.id)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6" /></svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>
                </span>
                <label class="m-0 inline-flex items-center gap-2 cursor-pointer flex-1">
                  <input
                      type="checkbox"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      :checked="areaState(area) === 'all'"
                      :indeterminate.prop="areaState(area) === 'some'"
                      @change="toggleArea(area, $event.target.checked)"
                  />
                  {{ area.name }}
                  <span class="text-xs font-normal text-gray-500">({{ areaMeterCount(area) }})</span>
                </label>
              </div>
            </li>

            <template v-if="expandedAreas.has(area.id)">
              <template v-for="zone in area.zones" :key="'z-' + zone.id">
                <li :class="areaIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                  <div
                      class="flex items-center gap-2 pr-4 py-3 pl-4 sm:pl-10 text-gray-800 hover:bg-gray-100 select-none"
                  >
                    <span class="text-gray-400 cursor-pointer p-1 -m-1" @click="toggleZoneExpanded(zone)">
                      <svg v-if="expandedZones.has(zone.id)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 9l6 6l6 -6" /></svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>
                    </span>
                    <label class="m-0 inline-flex items-center gap-2 cursor-pointer flex-1">
                      <input
                          type="checkbox"
                          class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          :checked="zoneState(zone) === 'all'"
                          :indeterminate.prop="zoneState(zone) === 'some'"
                          @change="toggleZone(zone, $event.target.checked)"
                      />
                      {{ zone.name }}
                      <span class="text-xs text-gray-500">({{ zone.meters.length }})</span>
                    </label>
                  </div>
                </li>

                <template v-if="expandedZones.has(zone.id)">
                  <li
                      v-for="meter in zone.meters"
                      :key="'m-' + meter.id"
                      class="bg-white"
                  >
                    <label class="m-0 flex items-start gap-2 pr-4 py-3 pl-6 sm:pl-20 cursor-pointer text-sm text-gray-700 hover:bg-gray-100">
                      <input
                          type="checkbox"
                          class="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          :checked="selectedMeterIds.has(meter.id)"
                          @change="toggleMeter(meter.id, $event.target.checked)"
                      />
                      <span class="flex-1">
                        {{ meter.name }}
                        <span v-if="meter.category" class="ml-1 inline-flex items-center px-3 py-0.5 text-sm rounded-full bg-gray-100 text-gray-600">{{ meter.category.name }}</span>
                        <span v-if="meter.tags && meter.tags.length > 0" class="ml-1">
                          <span
                              v-for="tag in meter.tags"
                              :key="tag.id"
                              class="mr-1 inline-flex items-center px-3 py-0.5 text-sm rounded-full"
                              :style="tagStyle(tag.color)"
                          >{{ tag.name }}</span>
                        </span>
                      </span>
                    </label>
                  </li>
                </template>
              </template>
            </template>
          </template>
        </ul>
        </div><!-- end tree panel -->

        <!-- Sidebar -->
        <div class="bg-white rounded-md border border-gray-200 p-4 self-start sticky top-4 space-y-4">

          <!-- Filters -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <p class="text-xs font-medium uppercase tracking-wider text-gray-500">Filters</p>
              <button
                  v-if="filterTagIds.length > 0 || filterCategoryId"
                  type="button"
                  class="text-xs text-blue-600 hover:text-blue-800"
                  @click="filterTagIds = []; filterCategoryId = null"
              >Clear</button>
            </div>
            <div class="space-y-2">
              <div>
                <p class="text-xs text-gray-500 mb-1">Category</p>
                <select2-group
                    :options="categoryOptions"
                    :close-on-select="true"
                    :allow-clear="true"
                    :multiple="false"
                    v-model="filterCategoryId"
                    placeholder="Any category..."
                ></select2-group>
              </div>
              <div>
                <p class="text-xs text-gray-500 mb-1">Tags</p>
                <select2-group
                    :options="filterTagOptionsGrouped"
                    :close-on-select="false"
                    :allow-clear="true"
                    :multiple="true"
                    v-model="filterTagIds"
                    placeholder="Any tags..."
                ></select2-group>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-100"></div>

          <div>
            <p class="text-xs font-medium uppercase tracking-wider text-gray-500">Point Actions</p>
            <p class="mt-1 text-sm text-gray-600">Select points from the tree, then choose an action.</p>
          </div>

          <div class="space-y-2">
            <button
                type="button"
                class="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-40"
                :disabled="selectedMeterIds.size === 0"
                @click="openTagsModal"
            >Manage Tags</button>
            <button
                type="button"
                class="w-full rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40"
                :disabled="selectedMeterIds.size === 0"
                @click="openCategoryModal"
            >Set Category</button>
          </div>

          <p v-if="selectedMeterIds.size === 0" class="text-xs text-gray-400 text-center">No points selected</p>
          <p v-else class="text-xs text-blue-600 text-center font-medium">{{ selectedMeterIds.size }} {{ selectedMeterIds.size === 1 ? 'point' : 'points' }} selected</p>
        </div>

      </div><!-- end grid -->
    </div>

    <!-- Manage Tags Modal -->
    <div v-if="showTagsModal" class="fixed inset-0 z-50 overflow-hidden">
      <div class="flex items-center justify-center min-h-screen px-4 py-8">
        <div class="fixed inset-0 bg-black opacity-30" @click="closeTagsModal"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]">

          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 class="m-0 p-0 text-left text-lg font-medium text-gray-900">
              Manage Tags
              <span class="ml-2 text-sm font-normal text-gray-500">({{ selectedMetersData.length }} {{ selectedMetersData.length === 1 ? 'point' : 'points' }})</span>
            </h3>
            <button @click="closeTagsModal" class="text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
            </button>
          </div>

          <!-- Bulk actions -->
          <div class="px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
            <p class="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">Bulk actions for all selected points</p>
            <p class="text-xs text-gray-500 mb-3">Add or remove a tag across every selected point at once.</p>
            <div class="flex gap-3 items-end">
              <div class="flex-1">
                <select2-group
                    :options="tagOptionsGrouped"
                    :close-on-select="true"
                    :allow-clear="true"
                    :multiple="false"
                    v-model="bulkTagId"
                    placeholder="Select a tag..."
                ></select2-group>
              </div>
              <button
                  type="button"
                  :disabled="!bulkTagId || bulkTagBusy"
                  class="inline-flex min-w-[6rem] items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  @click="bulkAddToAll"
              >Add to all</button>
              <button
                  type="button"
                  :disabled="!bulkTagId || bulkTagBusy"
                  class="inline-flex min-w-[6rem] items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  @click="bulkRemoveFromAll"
              >Remove from all</button>
            </div>
            <p v-if="modalBulkError" class="mt-2 text-sm text-red-600">{{ modalBulkError }}</p>
            <p v-if="modalBulkSuccess" class="mt-2 text-sm text-green-600">{{ modalBulkSuccess }}</p>
          </div>

          <!-- Per-meter list -->
          <div class="overflow-y-auto divide-y divide-gray-100">
            <div
                v-for="meter in liveModalMeters"
                :key="meter.id + '-' + (meter.tags || []).map(t => t.id).join(',')"
            >
              <!-- Tag pills row -->
              <div class="flex items-start gap-3 px-6 py-3">
                <span class="flex-1 text-sm text-gray-800 pt-0.5">
                  {{ meter.name }}
                  <span v-if="meter.category" class="ml-1 inline-flex items-center px-3 py-0.5 text-sm rounded-full bg-gray-100 text-gray-600">
                    {{ meter.category.name }}
                  </span>
                </span>
                <div class="flex flex-wrap gap-1.5 justify-end">
                  <div
                      v-for="tag in (meter.tags || [])"
                      :key="tag.id"
                      class="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 text-sm rounded-full"
                      :style="tagStyle(tag.color)"
                  >
                    {{ tag.name }}
                    <button
                        type="button"
                        class="flex items-center justify-center w-5 h-5 rounded-full hover:bg-black/10 transition-colors"
                        :disabled="!!perMeterBusy[meter.id + ':' + tag.id]"
                        @click="removeTagFromMeter(meter.id, tag.id)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <button
                      type="button"
                      class="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full border border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600"
                      @click="openPerMeterAdd(meter)"
                  >+ Add</button>
                </div>
              </div>

              <!-- Inline tag add drawer -->
              <div
                  v-if="perMeterAddTarget && perMeterAddTarget.id === meter.id"
                  class="flex items-center gap-3 px-6 py-3 bg-gray-50 border-t border-gray-200"
              >
                <div class="flex-1">
                  <select2-group
                      :options="tagOptionsGrouped"
                      :close-on-select="true"
                      :allow-clear="true"
                      :multiple="false"
                      v-model="perMeterAddTagId"
                      placeholder="Select a tag to add..."
                  ></select2-group>
                </div>
                <button
                    type="button"
                    :disabled="!perMeterAddTagId || perMeterAddBusy"
                    class="inline-flex min-w-[5rem] items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                    @click="confirmPerMeterAdd"
                >Add Tag</button>
                <button
                    type="button"
                    class="text-gray-400 hover:text-gray-600"
                    @click="perMeterAddTarget = null; perMeterAddTagId = null"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
                </button>
              </div>

            </div>
          </div>

          <!-- Footer -->
          <div class="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button @click="closeTagsModal" class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-6 sm:px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">Done</button>
          </div>

        </div>
      </div>
    </div>

    <!-- Set Category Modal -->
    <div v-if="showCategoryModal" class="fixed inset-0 z-50 overflow-hidden">
      <div class="flex items-center justify-center min-h-screen px-4 py-8">
        <div class="fixed inset-0 bg-black opacity-30" @click="closeCategoryModal"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col max-h-[85vh]">

          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 class="m-0 p-0 text-left text-lg font-medium text-gray-900">
              Set Meter Category
              <span class="ml-2 text-sm font-normal text-gray-500">({{ selectedMetersData.length }} {{ selectedMetersData.length === 1 ? 'point' : 'points' }})</span>
            </h3>
            <button @click="closeCategoryModal" class="text-gray-400 hover:text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
            </button>
          </div>

          <!-- Bulk set category -->
          <div class="px-6 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
            <p class="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">Set category for all selected points</p>
            <p class="text-xs text-gray-500 mb-3">This replaces the existing category on each point.</p>
            <div class="flex gap-3 items-end">
              <div class="flex-1">
                <select2-group
                    :options="categoryOptions"
                    :close-on-select="true"
                    :allow-clear="true"
                    :multiple="false"
                    v-model="bulkCategoryId"
                    placeholder="Select a category..."
                ></select2-group>
              </div>
              <button
                  type="button"
                  :disabled="!bulkCategoryId || bulkCategoryBusy"
                  class="inline-flex min-w-[6rem] items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  @click="bulkSetCategory"
              >Apply to all</button>
            </div>
            <p v-if="modalCategoryError" class="mt-2 text-sm text-red-600">{{ modalCategoryError }}</p>
            <p v-if="modalCategorySuccess" class="mt-2 text-sm text-green-600">{{ modalCategorySuccess }}</p>
          </div>

          <!-- Per-meter category list -->
          <div class="overflow-y-auto divide-y divide-gray-100">
            <div
                v-for="meter in selectedMetersData"
                :key="'cat-' + meter.id"
            >
              <!-- Category row -->
              <div class="flex items-center gap-3 px-6 py-3">
                <span class="flex-1 text-sm text-gray-800">{{ meter.name }}</span>
                <div class="flex items-center gap-2">
                  <span v-if="meter.category" class="inline-flex items-center px-3 py-0.5 text-sm rounded-full bg-gray-100 text-gray-600">{{ meter.category.name }}</span>
                  <span v-else class="text-xs text-gray-400 italic">No category</span>
                </div>
              </div>

            </div>
          </div>

          <!-- Footer -->
          <div class="flex justify-end px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button @click="closeCategoryModal" class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-6 sm:px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">Done</button>
          </div>

        </div>
      </div>
    </div>

    <!-- Create/Edit Tag Modal -->
    <div v-if="showTagModal" class="fixed inset-0 z-[1100] overflow-y-auto" @click.self="closeTagModal">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-black opacity-30"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="m-0 p-0 text-left text-lg font-medium text-gray-900 mb-4">{{ editingTag ? 'Edit Tag' : 'New Tag' }}</h3>

          <!-- A Simple/Keyed toggle for both creating and editing. Simple is a plain tag;
               keyed is a "key:value" tag. Editing a keyed tag pre-splits it into key + value. -->
          <div class="mb-4">
            <div class="inline-flex rounded-md border border-gray-300 p-0.5 mb-4 bg-gray-50">
              <button
                  type="button"
                  class="px-3 py-1 text-sm rounded transition-colors"
                  :class="tagForm.mode === 'simple' ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'"
                  @click="setTagMode('simple')"
              >Simple tag</button>
              <button
                  type="button"
                  class="px-3 py-1 text-sm rounded transition-colors"
                  :class="tagForm.mode === 'keyed' ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'"
                  @click="setTagMode('keyed')"
              >Key + value</button>
            </div>

            <!-- Simple tag -->
            <div v-if="tagForm.mode === 'simple'">
              <label class="block text-sm font-medium text-gray-700 mb-1">Tag Name</label>
              <input
                  v-model="tagForm.value"
                  type="text"
                  maxlength="100"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="e.g. Cold Storage, Clean Room, HVAC"
                  @keyup.enter="saveTag"
              />
            </div>

            <!-- Keyed tag: key + value, named "key:value" -->
            <div v-else class="flex items-center gap-2">
              <div class="w-2/5">
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-sm font-medium text-gray-700">Key</label>
                  <button type="button" class="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap" @click="openNewKeyModal">+ New key</button>
                </div>
                <select2
                    :key="keySelectKey"
                    :options="keyOptions"
                    :multiple="false"
                    :close-on-select="true"
                    :value="tagForm.key"
                    @update:value="tagForm.key = $event || ''"
                    placeholder="Select a key"
                ></select2>
              </div>
              <span class="pt-6 text-gray-400">:</span>
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">Value</label>
                <input
                    v-model="tagForm.value"
                    type="text"
                    maxlength="100"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    :placeholder="tagFormPlaceholder"
                    @keyup.enter="saveTag"
                />
              </div>
            </div>

            <p v-if="tagFormError" class="mt-1 text-sm text-red-600">{{ tagFormError }}</p>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div class="flex flex-wrap gap-2 mb-3">
              <button
                  v-for="swatch in tagPalette" :key="swatch"
                  type="button"
                  class="w-7 h-7 rounded-full border-2 transition-all"
                  :style="{ backgroundColor: swatch }"
                  :class="tagForm.color === swatch ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'"
                  @click="tagForm.color = swatch"
              ></button>
            </div>
            <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                :style="tagStyle(tagForm.color)"
            >{{ tagPreviewLabel }}</span>
          </div>
          <div class="flex justify-end gap-3">
            <button @click="closeTagModal" class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-6 sm:px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">Cancel</button>
            <button
                @click="saveTag"
                :disabled="tagSaving || !tagFormValid"
                class="inline-flex min-w-20 items-center justify-center rounded-md bg-blue-600 px-6 sm:px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >{{ tagSaving ? 'Saving...' : 'Save' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- New key modal (sits above the tag modal). Adds a key to the dropdown and selects it. -->
    <div v-if="showNewKeyModal" class="fixed inset-0 z-[1200] overflow-y-auto" @click.self="showNewKeyModal = false">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-black opacity-30"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
          <h3 class="m-0 p-0 text-left text-lg font-medium text-gray-900 mb-4">New Key</h3>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Key name</label>
            <input
                v-model="newKeyName"
                ref="newKeyModalInput"
                type="text"
                maxlength="50"
                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="e.g. Room, Calibration Date"
                @keyup.enter="confirmNewKey"
            />
            <p v-if="newKeyError" class="mt-1 text-sm text-red-600">{{ newKeyError }}</p>
          </div>
          <div class="flex justify-end gap-3">
            <button @click="showNewKeyModal = false" class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-6 sm:px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">Cancel</button>
            <button
                @click="confirmNewKey"
                :disabled="!newKeyName.trim()"
                class="inline-flex min-w-20 items-center justify-center rounded-md bg-blue-600 px-6 sm:px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >Add</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-[1100] overflow-y-auto" @click.self="showDeleteModal = false">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-black opacity-30"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
          <h3 class="m-0 p-0 text-left text-lg font-medium text-gray-900 mb-2">Delete Tag</h3>
          <p class="text-sm text-gray-600 mb-4">
            Delete <strong>{{ deletingTag && deletingTag.name }}</strong>? It will be removed from all {{ deletingTag && deletingTag.count }} point{{ deletingTag && deletingTag.count !== 1 ? 's' : '' }}.
          </p>
          <p v-if="deleteError" class="text-sm text-red-600 mb-3">{{ deleteError }}</p>
          <div class="flex justify-end gap-3">
            <button @click="showDeleteModal = false" class="inline-flex min-w-20 items-center justify-center rounded-md border border-gray-300 bg-white px-6 sm:px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50">Cancel</button>
            <button
                @click="deleteTag"
                :disabled="tagDeleting"
                class="inline-flex min-w-20 items-center justify-center rounded-md bg-red-600 px-6 sm:px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >{{ tagDeleting ? 'Deleting...' : 'Delete' }}</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import { SonicuCoreMixin } from '@/mixins/SonicuCoreMixin'
import SlideOverPanel from '@/components/elements/SlideOverPanel.vue'

export default {
  name: 'TagManagementIndex',
  mixins: [SonicuCoreMixin],
  components: { SlideOverPanel },

  props: {
    systemTags: { type: Array, default: () => [] },
  },

  data() {
    return {
      // Tag library
      showTagLibrary: false,
      tagsLoading:   false,
      systemTagList: this.systemTags.map(t => ({ ...t, count: 0 })),
      customerTags:  [],

      // Tag modal (create/edit)
      showTagModal:  false,
      editingTag:    null,
      tagPalette: [
        '#6366f1', // indigo
        '#3b82f6', // blue
        '#14b8a6', // teal
        '#22c55e', // green
        '#eab308', // yellow
        '#f97316', // orange
        '#ef4444', // red
        '#ec4899', // pink
        '#a855f7', // purple
      ],
      tagForm:            { value: '', key: '', mode: 'simple', color: '#6366f1' },
      tagFormError:       '',
      tagFormPlaceholder: 'e.g. Building A, Cold Storage, HVAC',
      tagSaving:     false,
      keySelectKey:   0,       // bump to force the key select2 to re-render
      localKeys:      [],      // keys created this session (slugs); appear in the dropdown
      showNewKeyModal: false,
      newKeyName:     '',
      newKeyError:    '',

      // Delete modal
      showDeleteModal: false,
      deletingTag:     null,
      tagDeleting:     false,
      deleteError:     null,

      // Tree
      tree:            [],
      treeLoading:     false,
      treeLoadError:   null,
      treeSearch:      '',
      filterTagIds:    [],
      filterCategoryId: null,
      expandedAreas:   new Set(),
      expandedZones:   new Set(),

      // Selection
      selectedMeterIds: new Set(),

      // Manage Tags modal
      showTagsModal:      false,
      bulkTagId:          null,
      bulkTagBusy:        false,
      modalBulkError:     '',
      modalBulkSuccess:   '',
      perMeterBusy:       {},
      perMeterAddTarget:  null,
      perMeterAddTagId:   null,
      perMeterAddBusy:    false,

      // Set Category modal
      showCategoryModal:    false,
      bulkCategoryId:       null,
      bulkCategoryBusy:     false,
      modalCategoryError:   '',
      modalCategorySuccess: '',

    }
  },

  computed: {
    allTags() {
      return this.customerTags
    },

    tagSuggestions() {
      // Each suggestion is a keyed-tag key. Picking one creates a tag named "key:value".
      return [
        { label: 'Model',             key: 'model',            placeholder: 'e.g. Model number or series name' },
        { label: 'Manufacturer',      key: 'manufacturer',     placeholder: 'e.g. Manufacturer or brand name' },
        { label: 'Serial Number',     key: 'serial_number',    placeholder: 'e.g. SN-12345' },
        { label: 'Location',          key: 'location',         placeholder: 'e.g. Building A, Room 204' },
        { label: 'Last Maintenance',  key: 'last_maintenance', placeholder: 'e.g. 2024-01-15, Q1 2025' },
      ]
    },

    // Key dropdown options (flat): suggested keys, any keys already in use, and any keys
    // created this session via the New Key modal. Deduped, slug -> Title Case label.
    keyOptions() {
      const opts = {}
      const add = (slug) => {
        if (slug && !opts[slug]) {
          opts[slug] = slug.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        }
      }
      this.tagSuggestions.forEach(s => { opts[s.key] = s.label })
      this.customerTags.forEach(t => add(t.tag_key))
      this.localKeys.forEach(add)
      return opts
    },

    tagFormValid() {
      if (this.tagForm.mode === 'keyed') return !!this.tagForm.key.trim() && !!this.tagForm.value.trim()
      return !!this.tagForm.value.trim()
    },

    tagPreviewLabel() {
      const value = this.tagForm.value.trim()
      if (!value) return 'Preview'
      if (this.tagForm.mode === 'keyed') {
        return this.tagFormKeyDisplay ? `${this.tagFormKeyDisplay}:${value}` : value
      }
      return value
    },

    // The display key shown in the name: lowercase slug, title-cased ('serial_number' -> 'Serial Number').
    // Mirrors the server so the preview matches what gets stored.
    tagFormKeyDisplay() {
      const slug = this.tagForm.key.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      if (!slug) return ''
      return slug.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    },

    tagOptionsGrouped() {
      const customerGroup = {}
      this.customerTags.forEach(t => { customerGroup[t.id.toString()] = t.name })
      return Object.keys(customerGroup).length ? { 'Custom Tags': customerGroup } : {}
    },

    // Filter dropdown: plain tags under "Custom Tags", keyed tags grouped by their key
    // with an "All <Key>" option (value "all:<slug>") that selects every value of that key.
    filterTagOptionsGrouped() {
      const plain  = {}
      const keyed  = {} // slug -> { label, options: {id: name} }

      this.customerTags.forEach(t => {
        if (t.tag_key) {
          if (!keyed[t.tag_key]) {
            const label = t.tag_key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            keyed[t.tag_key] = { label, options: {} }
          }
          keyed[t.tag_key].options[t.id.toString()] = t.name
        } else {
          plain[t.id.toString()] = t.name
        }
      })

      const groups = {}
      if (Object.keys(plain).length) groups['Custom Tags'] = plain

      Object.keys(keyed).sort().forEach(slug => {
        const { label, options } = keyed[slug]
        // "All <Key>" first, then each value.
        groups[label] = { [`all:${slug}`]: `All ${label}`, ...options }
      })

      return groups
    },

    categoryOptions() {
      const opts = {}
      this.systemTagList.forEach(t => { opts[t.id.toString()] = t.name })
      return { 'Meter Categories': opts }
    },

    allExpanded() {
      return this.tree.length > 0 && this.tree.every(a => this.expandedAreas.has(a.id))
    },

    filteredTree() {
      const q          = this.treeSearch.trim().toLowerCase()
      const tagIds     = this.filterTagIds.map(Number)
      const categoryId = this.filterCategoryId ? Number(this.filterCategoryId) : null

      const matchesMeter = (m) => {
        if (q && !m.name.toLowerCase().includes(q)) return false
        if (tagIds.length > 0 && !tagIds.some(id => (m.tags || []).some(t => t.id === id))) return false
        if (categoryId !== null && m.category?.id !== categoryId) return false
        return true
      }

      if (!q && tagIds.length === 0 && categoryId === null) return this.tree

      return this.tree.map(area => {
        const zones = area.zones.map(zone => {
          const meters = zone.meters.filter(matchesMeter)
          return { ...zone, meters }
        }).filter(z => z.meters.length > 0)

        if (zones.length === 0) return null
        return { ...area, zones }
      }).filter(Boolean)
    },

    selectedMetersData() {
      return this.tree.flatMap(a => a.zones.flatMap(z => z.meters)).filter(m => this.selectedMeterIds.has(m.id))
    },

    liveModalMeters() {
      return this.selectedMetersData
    },
  },

  created() {
    this.loadTags()
    this.loadTree()
  },

  mounted() {
    this._onTagLibraryOpen = () => { this.showTagLibrary = true }
    window.addEventListener('tag-library:open', this._onTagLibraryOpen)
  },

  beforeUnmount() {
    window.removeEventListener('tag-library:open', this._onTagLibraryOpen)
  },

  watch: {
    filteredTree() {
      const visibleIds = new Set(this.filteredTree.flatMap(a => a.zones.flatMap(z => z.meters.map(m => m.id))))
      const next = new Set([...this.selectedMeterIds].filter(id => visibleIds.has(id)))
      if (next.size !== this.selectedMeterIds.size) this.selectedMeterIds = next
    },

    // Expand any "All <Key>" selections (value "all:<slug>") into the real tag ids for that key.
    filterTagIds(ids) {
      const allKeys = ids.filter(v => typeof v === 'string' && v.startsWith('all:'))
      if (allKeys.length === 0) return

      const expanded = new Set(ids.filter(v => !(typeof v === 'string' && v.startsWith('all:'))))
      allKeys.forEach(v => {
        const slug = v.slice(4)
        this.customerTags
          .filter(t => t.tag_key === slug)
          .forEach(t => expanded.add(t.id.toString()))
      })

      this.filterTagIds = [...expanded]
    },
  },

  methods: {
    tagStyle(color) {
      if (!color) return { backgroundColor: '#f3f4f6', color: '#4b5563' }
      const r = parseInt(color.slice(1, 3), 16)
      const g = parseInt(color.slice(3, 5), 16)
      const b = parseInt(color.slice(5, 7), 16)
      const textColor = {
        '#6366f1': '#4338ca',
        '#3b82f6': '#1d4ed8',
        '#14b8a6': '#0f766e',
        '#22c55e': '#166534',
        '#eab308': '#854d0e',
        '#f97316': '#9a3412',
        '#ef4444': '#b91c1c',
        '#ec4899': '#be185d',
        '#a855f7': '#7e22ce',
      }[color] ?? color
      return { backgroundColor: `rgba(${r}, ${g}, ${b}, 0.15)`, color: textColor }
    },


    // -- Tag library ----------------------------------------------------------

    loadTags() {
      this.tagsLoading = true
      axios.get(this.company.buildCompanyApiInternalRoute('tags'))
        .then(res => {
          this.systemTagList = res.data.system_tags
          this.customerTags  = res.data.customer_tags
        })
        .catch(() => {})
        .finally(() => { this.tagsLoading = false })
    },

    openCreateModal(suggestion = null) {
      this.editingTag         = null
      // Simple is the default. A suggestion opens straight into keyed mode with the
      // key prefilled; the user can still switch back to Simple.
      this.tagForm            = {
        value: '',
        key: suggestion ? suggestion.key : '',
        mode: suggestion ? 'keyed' : 'simple',
        color: '#6366f1',
      }
      this.tagFormError       = ''
      this.tagFormPlaceholder = suggestion ? suggestion.placeholder : 'e.g. 204, Helmer iLR125'
      this.keySelectKey++
      this.showTagModal       = true
    },

    setTagMode(mode) {
      this.tagForm.mode = mode
      this.tagFormError = ''
    },

    openNewKeyModal() {
      this.newKeyName     = ''
      this.newKeyError    = ''
      this.showNewKeyModal = true
      this.$nextTick(() => this.$refs.newKeyModalInput && this.$refs.newKeyModalInput.focus())
    },

    confirmNewKey() {
      const slug = this.newKeyName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      if (!slug) {
        this.newKeyError = 'Enter a key name.'
        return
      }

      // Add to the session key list (persists for real once a tag uses it), then select it.
      if (!this.localKeys.includes(slug)) this.localKeys.push(slug)
      this.tagForm.key = slug
      this.keySelectKey++ // re-render the select so the new key shows as selected
      this.showNewKeyModal = false
    },

    openEditModal(tag) {
      this.editingTag = tag

      if (tag.tag_key) {
        // Keyed tag: name is "Display Key:Value"; the value is everything after the first colon.
        const colon = tag.name.indexOf(':')
        const value = colon === -1 ? tag.name : tag.name.slice(colon + 1)
        this.tagForm = { value, key: tag.tag_key, mode: 'keyed', color: tag.color ?? '#6366f1' }
      } else {
        this.tagForm = { value: tag.name, key: '', mode: 'simple', color: tag.color ?? '#6366f1' }
      }

      this.tagFormError       = ''
      this.tagFormPlaceholder = 'e.g. 204, Helmer iLR125'
      this.keySelectKey++
      this.showTagModal       = true
    },

    closeTagModal() {
      this.showTagModal = false
      this.editingTag   = null
      this.tagFormError = ''
    },

    saveTag() {
      if (!this.tagFormValid) return
      this.tagSaving    = true
      this.tagFormError = ''

      // Keyed -> {key, value} ("key:value" name); simple -> {name}. Same shape for
      // create and edit, so editing can also switch a tag between the two types.
      const payload = this.tagForm.mode === 'keyed'
        ? { key: this.tagForm.key.trim(), value: this.tagForm.value.trim(), color: this.tagForm.color }
        : { name: this.tagForm.value.trim(), color: this.tagForm.color }

      const req = this.editingTag
        ? axios.patch(this.company.buildCompanyApiInternalRoute(`tags/${this.editingTag.id}`), payload)
        : axios.post(this.company.buildCompanyApiInternalRoute('tags'), payload)

      req.then(res => {
          if (this.editingTag) {
            const idx = this.customerTags.findIndex(t => t.id === this.editingTag.id)
            if (idx !== -1) this.customerTags.splice(idx, 1, res.data)

            this.tree = this.tree.map(area => ({
              ...area,
              zones: area.zones.map(zone => ({
                ...zone,
                meters: zone.meters.map(m => ({
                  ...m,
                  tags: (m.tags || []).map(t => t.id === res.data.id ? { ...t, name: res.data.name, color: res.data.color } : t),
                })),
              })),
            }))
          } else {
            this.customerTags.push(res.data)
          }
          this.closeTagModal()
        })
        .catch(err => {
          this.tagFormError = err.response?.data?.message ?? 'An error occurred.'
        })
        .finally(() => { this.tagSaving = false })
    },

    confirmDelete(tag) {
      this.deletingTag     = tag
      this.deleteError     = null
      this.showDeleteModal = true
    },

    deleteTag() {
      this.tagDeleting = true
      axios.delete(this.company.buildCompanyApiInternalRoute(`tags/${this.deletingTag.id}`))
        .then(() => {
          this.customerTags    = this.customerTags.filter(t => t.id !== this.deletingTag.id)
          this.showDeleteModal = false
          this.deletingTag     = null
          this.loadTree()
        })
        .catch(err => {
          this.deleteError = err.response?.data?.message ?? 'Failed to delete tag.'
        })
        .finally(() => { this.tagDeleting = false })
    },

    // -- Tree -----------------------------------------------------------------

    loadTree(silent = false) {
      if (!silent) this.treeLoading = true
      if (!silent) this.treeLoadError = null
      axios.get(this.company.buildCompanyApiInternalRoute('tags/meters'))
        .then(res => {
          this.tree = res.data.areas
        })
        .catch(() => { if (!silent) this.treeLoadError = 'Failed to load points. Please refresh.' })
        .finally(() => { this.treeLoading = false })
    },

    // -- Tree expand/collapse -------------------------------------------------

    toggleAreaExpanded(area) {
      const next = new Set(this.expandedAreas)
      next.has(area.id) ? next.delete(area.id) : next.add(area.id)
      this.expandedAreas = next
    },

    toggleZoneExpanded(zone) {
      const next = new Set(this.expandedZones)
      next.has(zone.id) ? next.delete(zone.id) : next.add(zone.id)
      this.expandedZones = next
    },

    toggleAllExpanded() {
      if (this.allExpanded) {
        this.expandedAreas = new Set()
        this.expandedZones = new Set()
      } else {
        this.expandedAreas = new Set(this.tree.map(a => a.id))
        this.expandedZones = new Set(this.tree.flatMap(a => a.zones.map(z => z.id)))
      }
    },

    // -- Selection ------------------------------------------------------------

    areaMeterCount(area) {
      return area.zones.reduce((sum, z) => sum + z.meters.length, 0)
    },

    areaState(area) {
      const allIds  = area.zones.flatMap(z => z.meters.map(m => m.id))
      const checked = allIds.filter(id => this.selectedMeterIds.has(id))
      if (checked.length === 0)             return 'none'
      if (checked.length === allIds.length) return 'all'
      return 'some'
    },

    zoneState(zone) {
      const allIds  = zone.meters.map(m => m.id)
      const checked = allIds.filter(id => this.selectedMeterIds.has(id))
      if (checked.length === 0)             return 'none'
      if (checked.length === allIds.length) return 'all'
      return 'some'
    },

    toggleArea(area, checked) {
      const next = new Set(this.selectedMeterIds)
      area.zones.forEach(z => z.meters.forEach(m => { checked ? next.add(m.id) : next.delete(m.id) }))
      this.selectedMeterIds = next
    },

    toggleZone(zone, checked) {
      const next = new Set(this.selectedMeterIds)
      zone.meters.forEach(m => { checked ? next.add(m.id) : next.delete(m.id) })
      this.selectedMeterIds = next
    },

    toggleMeter(id, checked) {
      const next = new Set(this.selectedMeterIds)
      checked ? next.add(id) : next.delete(id)
      this.selectedMeterIds = next
    },

    clearSelection() {
      this.selectedMeterIds = new Set()
    },

    // -- Manage Tags modal ----------------------------------------------------

    openTagsModal() {
      this.bulkTagId                  = null
      this.bulkTagBusy                = false
      this.modalBulkError             = ''
      this.perMeterBusy               = {}
      this.perMeterAddTarget          = null
      this.perMeterAddTagId           = null
      this.showTagsModal              = true
    },

    closeTagsModal() {
      this.showTagsModal     = false
      this.perMeterAddTarget = null
    },

    flashSuccess(field, message) {
      this[field] = message
      setTimeout(() => { this[field] = '' }, 3000)
    },

    bulkAddToAll() {
      this.modalBulkError = ''
      this.bulkTagBusy    = true
      axios.post(this.company.buildCompanyApiInternalRoute('tags/bulk'), {
        action:    'apply',
        tag_id:    parseInt(this.bulkTagId),
        meter_ids: Array.from(this.selectedMeterIds),
      })
        .then(() => {
          this.loadTree(true)
          this.flashSuccess('modalBulkSuccess', 'Tag applied to all selected points.')
        })
        .catch(err => { this.modalBulkError = err.response?.data?.message ?? 'Failed.' })
        .finally(() => { this.bulkTagBusy = false })
    },

    bulkRemoveFromAll() {
      this.modalBulkError = ''
      this.bulkTagBusy    = true
      axios.post(this.company.buildCompanyApiInternalRoute('tags/bulk'), {
        action:    'remove',
        tag_id:    parseInt(this.bulkTagId),
        meter_ids: Array.from(this.selectedMeterIds),
      })
        .then(() => {
          this.loadTree(true)
          this.flashSuccess('modalBulkSuccess', 'Tag removed from all selected points.')
        })
        .catch(err => { this.modalBulkError = err.response?.data?.message ?? 'Failed.' })
        .finally(() => { this.bulkTagBusy = false })
    },

    removeTagFromMeter(meterId, tagId) {
      // Optimistically remove the tag from the tree immediately
      const removedTag = { id: tagId }
      this.tree = this.tree.map(area => ({
        ...area,
        zones: area.zones.map(zone => ({
          ...zone,
          meters: zone.meters.map(m =>
            m.id === meterId
              ? { ...m, tags: m.tags.filter(t => t.id !== tagId) }
              : m
          ),
        })),
      }))

      axios.post(this.company.buildCompanyApiInternalRoute('tags/bulk'), {
        action:    'remove',
        tag_id:    tagId,
        meter_ids: [meterId],
      })
        .catch(() => {
          // Restore the tag on failure
          this.tree = this.tree.map(area => ({
            ...area,
            zones: area.zones.map(zone => ({
              ...zone,
              meters: zone.meters.map(m => {
                if (m.id !== meterId) return m
                const tag = this.customerTags.find(t => t.id === tagId)
                if (!tag) return m
                return { ...m, tags: [...m.tags, { id: tag.id, name: tag.name, color: tag.color }] }
              }),
            })),
          }))
          this.flashSuccess('modalBulkSuccess', '')
          this.modalBulkError = 'Failed to remove tag. Please try again.'
          setTimeout(() => { this.modalBulkError = '' }, 3000)
        })
    },

    openPerMeterAdd(meter) {
      this.perMeterAddTarget = meter
      this.perMeterAddTagId  = null
    },

    confirmPerMeterAdd() {
      if (!this.perMeterAddTagId) return
      this.perMeterAddBusy = true
      axios.post(this.company.buildCompanyApiInternalRoute('tags/bulk'), {
        action:    'apply',
        tag_id:    parseInt(this.perMeterAddTagId),
        meter_ids: [this.perMeterAddTarget.id],
      })
        .then(() => {
          this.perMeterAddTarget = null
          this.perMeterAddTagId  = null
          this.loadTree(true)
          this.flashSuccess('modalBulkSuccess', 'Tag added successfully.')
        })
        .catch(() => {})
        .finally(() => { this.perMeterAddBusy = false })
    },

    // -- Set Category modal ---------------------------------------------------

    openCategoryModal() {
      this.bulkCategoryId       = null
      this.bulkCategoryBusy     = false
      this.modalCategoryError   = ''
      this.modalCategorySuccess = ''
      this.showCategoryModal    = true
    },

    closeCategoryModal() {
      this.showCategoryModal = false
      this.loadTree(true)
    },

    bulkSetCategory() {
      this.modalCategoryError   = ''
      this.modalCategorySuccess = ''
      this.bulkCategoryBusy     = true
      const tagId = parseInt(this.bulkCategoryId)
      const categoryTag = this.systemTagList.find(t => t.id === tagId)
      axios.post(this.company.buildCompanyApiInternalRoute('tags/bulk'), {
        action:    'set_category',
        tag_id:    tagId,
        meter_ids: Array.from(this.selectedMeterIds),
      })
        .then(() => {
          if (categoryTag) {
            this.tree = this.tree.map(area => ({
              ...area,
              zones: area.zones.map(zone => ({
                ...zone,
                meters: zone.meters.map(meter =>
                  this.selectedMeterIds.has(meter.id)
                    ? { ...meter, category: { id: categoryTag.id, name: categoryTag.name } }
                    : meter
                ),
              })),
            }))
          }
          this.bulkCategoryId = null
          this.flashSuccess('modalCategorySuccess', 'Category applied to all selected points.')
        })
        .catch(err => { this.modalCategoryError = err.response?.data?.message ?? 'Failed.' })
        .finally(() => { this.bulkCategoryBusy = false })
    },

  },
}
</script>
